/**
 * Modal System for Wodify Theme
 * Handles modal opening/closing via URL parameters and user interactions
 * // Open a modal
    WodifyModal.open('.c-modal--1');

    // Close current modal
    WodifyModal.close();

    // Close all modals
    WodifyModal.closeAll();

    // Check if modal is open
    if (WodifyModal.isOpen()) { ... }
 */

document.addEventListener('DOMContentLoaded', function() {
    
    // Modal state management
    let currentModal = null;
    let isModalOpen = false;
    
    // Get all modals
    const modals = document.querySelectorAll('.c-modal');
    
    // Initialize modal system
    initializeModals();
    
    /**
     * Initialize all modal functionality
     */
    function initializeModals() {
        // Check URL for modal parameter on page load
        checkUrlForModal();
        
        // Set up event listeners for all modals
        modals.forEach(modal => {
            setupModalEventListeners(modal);
        });
        
        // Listen for URL changes (back/forward navigation)
        window.addEventListener('popstate', handlePopState);
        
        // Listen for clicks on links that might trigger modals
        document.addEventListener('click', handleLinkClick);
        
        // Listen for escape key
        document.addEventListener('keydown', handleKeyDown);
    }
    
    /**
     * Check URL parameters for modal trigger
     */
    function checkUrlForModal() {
        const urlParams = new URLSearchParams(window.location.search);
        const modalParam = urlParams.get('modal');
        
        if (modalParam) {
            const targetModal = findModalByParam(modalParam);
            if (targetModal) {
                openModal(targetModal);
            }
        }
    }
    
    /**
     * Find modal by URL parameter
     */
    function findModalByParam(param) {
        return document.querySelector(`[data-url-param="${param}"]`);
    }
    
    /**
     * Set up event listeners for a specific modal
     */
    function setupModalEventListeners(modal) {
        const closeBtn = modal.querySelector('.c-modal__close');
        const overlay = modal.querySelector('.c-modal__overlay');
        const content = modal.querySelector('.c-modal__content');
        
        // Close button click
        if (closeBtn) {
            closeBtn.addEventListener('click', function(e) {
                e.preventDefault();
                closeModal(modal);
            });
        }
        
        // Overlay click (clicking outside content)
        if (overlay) {
            overlay.addEventListener('click', function(e) {
                if (e.target === overlay) {
                    closeModal(modal);
                }
            });
        }
        
        // Prevent modal from closing when clicking inside content
        if (content) {
            content.addEventListener('click', function(e) {
                e.stopPropagation();
            });
        }
        
        // Also listen for clicks on the modal itself (in case overlay isn't clicked)
        modal.addEventListener('click', function(e) {
            if (e.target === modal) {
                closeModal(modal);
            }
        });
    }
    
    /**
     * Handle link clicks that might trigger modals
     */
    function handleLinkClick(e) {
        const link = e.target.closest('a');
        if (!link) return;
        
        const href = link.getAttribute('href');
        if (!href) return;
        
        // Check if link contains modal parameter
        const url = new URL(href, window.location.origin);
        const modalParam = url.searchParams.get('modal');
        
        if (modalParam) {
            e.preventDefault();
            
            const targetModal = findModalByParam(modalParam);
            if (targetModal) {
                // Update URL without page reload
                const newUrl = new URL(window.location);
                newUrl.searchParams.set('modal', modalParam);
                window.history.pushState({ modal: modalParam }, '', newUrl);
                
                openModal(targetModal);
            }
        }
    }
    
    /**
     * Handle browser back/forward navigation
     */
    function handlePopState(e) {
        if (e.state && e.state.modal) {
            const targetModal = findModalByParam(e.state.modal);
            if (targetModal) {
                openModal(targetModal);
            }
        } else {
            // No modal in state, close any open modals
            if (currentModal) {
                closeModal(currentModal, false); // Don't update history
            }
        }
    }
    
    /**
     * Handle keyboard events
     */
    function handleKeyDown(e) {
        // Close modal on Escape key
        if (e.key === 'Escape' && isModalOpen && currentModal) {
            closeModal(currentModal);
        }
    }
    
    /**
     * Open a modal
     */
    function openModal(modal) {
        if (!modal) return;
        
        // Close any currently open modal
        if (currentModal && currentModal !== modal) {
            closeModal(currentModal, false);
        }
        
        // Set current modal
        currentModal = modal;
        isModalOpen = true;
        
        // Add classes for animation
        modal.classList.add('modal-enter');
        modal.style.display = 'flex';
        
        // Prevent body scroll
        document.body.classList.add('modal-open');
        
        // Trigger animation
        requestAnimationFrame(() => {
            modal.classList.add('modal-enter-active');
            modal.classList.remove('modal-enter');
        });
        
        // Focus management for accessibility
        const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
        
        // Dispatch custom event
        modal.dispatchEvent(new CustomEvent('modal:opened', {
            detail: { modal: modal }
        }));
    }
    
    /**
     * Close a modal
     */
    function closeModal(modal, updateHistory = true) {
        if (!modal) return;
        
        // Add exit animation classes
        modal.classList.add('modal-exit');
        modal.classList.remove('modal-enter-active');
        
        // Wait for animation to complete
        setTimeout(() => {
            modal.style.display = 'none';
            modal.classList.remove('modal-exit', 'modal-enter', 'modal-enter-active');
            
            // Reset state
            if (currentModal === modal) {
                currentModal = null;
                isModalOpen = false;
            }
            
            // Re-enable body scroll
            document.body.classList.remove('modal-open');
            
            // Update URL if needed
            if (updateHistory) {
                const newUrl = new URL(window.location);
                newUrl.searchParams.delete('modal');
                window.history.pushState({}, '', newUrl);
            }
            
            // Dispatch custom event
            modal.dispatchEvent(new CustomEvent('modal:closed', {
                detail: { modal: modal }
            }));
            
        }, 200); // Match CSS animation duration
    }
    
    /**
     * Public API for programmatic control
     */
    window.WodifyModal = {
        open: function(modalSelector) {
            const modal = typeof modalSelector === 'string' 
                ? document.querySelector(modalSelector)
                : modalSelector;
            if (modal) {
                openModal(modal);
            }
        },
        
        close: function(modalSelector = null) {
            if (modalSelector) {
                const modal = typeof modalSelector === 'string' 
                    ? document.querySelector(modalSelector)
                    : modalSelector;
                if (modal) {
                    closeModal(modal);
                }
            } else if (currentModal) {
                closeModal(currentModal);
            }
        },
        
        closeAll: function() {
            modals.forEach(modal => {
                if (modal.style.display !== 'none') {
                    closeModal(modal, false);
                }
            });
        },
        
        isOpen: function() {
            return isModalOpen;
        },
        
        getCurrent: function() {
            return currentModal;
        }
    };
    
    // Debug logging
    console.log(`🎭 Modal system initialized with ${modals.length} modals`);
});